import { DataModelsUserAccountType, SvcGetActiveShoppingCart, SvcGetShoppingCart } from "@springtree/eva-services-core";
import { useQuery } from "@tanstack/react-query";
import { useSetAtom } from "jotai";
import { useEffect, useRef } from "react";

import { shoppingCartRequestAtom } from "~/atoms/cart";
import { useAuthContext } from "~/contexts/auth";
import { useCartWithPendingStatus } from "~/hooks/use-cart";
import { useEvaService } from "~/hooks/use-eva-service";
import Logger from "~/services/logger/logger";

export default function GetActiveShoppingCart() {
  const { isAuthenticated, user } = useAuthContext();
  const { cart, isLoadingCart } = useCartWithPendingStatus();
  const getActiveShoppingCart = useEvaService(SvcGetActiveShoppingCart);
  const getShoppingCart = useEvaService(SvcGetShoppingCart);
  const setCurrentOrder = useSetAtom(shoppingCartRequestAtom);
  const clearedRef = useRef(!1);
  const cartStableRef = useRef(!1);

  useEffect(() => {
    /**
     * La query deve partire solo se non ho un carrello, oppure se quel carrello contiene un account di tipo standard.
     * Questo perché a quel punto, il carrello guest (con o senza account guest) viene riassegnato all'account loggato.
     */
    cartStableRef.current =
      !isLoadingCart &&
      (!cart?.ShoppingCart?.ID || cart?.ShoppingCart?.Customer?.AccountType === DataModelsUserAccountType.Standard);
  }, [isLoadingCart, cart?.ShoppingCart?.ID, cart?.ShoppingCart?.Customer?.AccountType]);

  const activeCartQuery = useQuery({
    queryKey: ["activeCart", user?.EmailAddress],
    queryFn: async () => {
      return getActiveShoppingCart().then(async (response) => {
        let activeCartId: string | null | "CLEAR" = response?.OrderID ? `${response.OrderID}` : null;
        if (activeCartId) {
          const activeCart = await getShoppingCart({
            OrderID: activeCartId,
          });
          // Workaround to exclude COD orders (and maybe even Ideal)
          if (activeCart?.ShoppingCart?.IsPlaced) {
            return "CLEAR";
          }

          if (activeCartId === cart?.ShoppingCart?.ID) {
            return null;
          }

          return activeCartId;
        } else {
          if (cart?.ShoppingCart?.IsPlaced) {
            return "CLEAR";
          }
        }

        return null;
      });
    },
    enabled: isAuthenticated && cartStableRef.current,
  });

  useEffect(() => {
    if (isLoadingCart || !activeCartQuery.data) {
      return;
    }

    if (activeCartQuery.data === "CLEAR") {
      if (!clearedRef.current) {
        Logger.instance.debug("GetActiveShoppingCart: clearing cart", {
          active: activeCartQuery.data,
        });
        clearedRef.current = !0;
        setCurrentOrder({ OrderID: void 0 });
      }
      return;
    }
    clearedRef.current = !1; // Reset the clear flag when the cart is valid
    if (activeCartQuery.data !== cart?.ShoppingCart?.ID) {
      Logger.instance.info(`GetActiveShoppingCart: overwrite ${cart?.ShoppingCart?.ID} with ${activeCartQuery.data}`);
      setCurrentOrder({ OrderID: activeCartQuery.data });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCartQuery.data, isLoadingCart]);

  return null;
}
